import { BACKEND_URL } from "../config";
import React, { useState, useEffect } from 'react';

const API = BACKEND_URL;

const pct = (v) => (v === null || v === undefined ? '—' : `${v}%`);
const num = (v) => (v === null || v === undefined ? '—' : v);
const dirMark = (c) => (c === true ? '✅' : c === false ? '❌' : '—');

const StatCard = ({ label, value, sub, accent, testid }) => (
  <div data-testid={testid} style={{
    flex: '1 1 180px', background: 'white', border: `2px solid ${accent}22`,
    borderLeft: `6px solid ${accent}`, borderRadius: '12px', padding: '1.1rem 1.25rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
  }}>
    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
    <div style={{ fontSize: '2rem', fontWeight: 800, color: accent, lineHeight: 1.1, marginTop: '0.35rem' }}>{value}</div>
    {sub && <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: '0.35rem' }}>{sub}</div>}
  </div>
);

const TierBlock = ({ title, emoji, s, accent }) => {
  if (!s) return null;
  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <h3 style={{ fontSize: '1.1rem', color: '#0f172a', marginBottom: '0.75rem' }}>{emoji} {title}
        <span style={{ fontSize: '0.85rem', color: '#94a3b8', fontWeight: 500 }}> · {s.total} fires</span>
      </h3>
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        <StatCard testid={`ei-${title.replace(/\s/g,'').toLowerCase()}-winrate`} label="Win Rate" value={pct(s.win_rate)} accent={accent}
          sub={`${s.wins}W · ${s.losses}L · ${s.stalls} stall`} />
        <StatCard testid={`ei-${title.replace(/\s/g,'').toLowerCase()}-tradedacc`} label="Traded Dir Accuracy" value={pct(s.traded_direction_accuracy)} accent="#0ea5e9"
          sub={`${s.traded_correct}/${s.determined} correct`} />
        <StatCard testid={`ei-${title.replace(/\s/g,'').toLowerCase()}-rawacc`} label="Raw Signal Accuracy" value={pct(s.raw_signal_accuracy)} accent="#8b5cf6"
          sub={`${s.raw_correct}/${s.determined} correct`} />
        <StatCard label="Pending" value={num(s.pending)} accent="#f59e0b" sub="not yet graded" />
      </div>
    </div>
  );
};

const EarlyIgnitionTierStats = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);

  const fetchStats = async (d) => {
    setLoading(true); setError(null);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API}/api/early-ignition/tier-stats?days=${d}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      if (json.success) setData(json);
      else setError(json.error || 'Failed to load tier stats');
    } catch (e) { setError(String(e)); }
    setLoading(false);
  };

  useEffect(() => { fetchStats(days); /* eslint-disable-next-line */ }, [days]);

  const sm = data?.summary || {};

  return (
    <div data-testid="ei-tier-stats-panel">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '0.5rem' }}>
        <h2 style={{ fontSize: '1.75rem', color: '#0c4a6e', margin: 0 }}>🚀 Early-Ignition Tier Stats</h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <select data-testid="ei-days-select" value={days} onChange={(e) => setDays(Number(e.target.value))}
            style={{ padding: '0.5rem 0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontWeight: 600 }}>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button data-testid="ei-refresh-btn" onClick={() => fetchStats(days)}
            style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: 'none', background: '#0ea5e9', color: 'white', fontWeight: 700, cursor: 'pointer' }}>
            🔄 Refresh
          </button>
        </div>
      </div>
      <p style={{ color: '#64748b', marginTop: 0, marginBottom: '1.5rem', fontSize: '0.9rem' }}>
        Read-only. Measures whether the <b>raw scanner-signal direction</b> and the <b>notified (traded) direction</b> were correct,
        split by Tier 1 (follow) vs Tier 2 (invert). Direction correctness comes from recorded favorable-vs-adverse pip excursions. No scanner logic is touched.
      </p>

      {loading && <div style={{ color: '#64748b' }}>Loading…</div>}
      {error && <div data-testid="ei-error" style={{ color: '#dc2626', fontWeight: 600 }}>⚠️ {error}</div>}

      {!loading && !error && data && (
        <>
          <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '12px', padding: '1rem 1.25rem', marginBottom: '1.5rem' }}>
            <span style={{ fontWeight: 700, color: '#0369a1' }}>Overall raw-signal accuracy: </span>
            <span data-testid="ei-raw-accuracy-all" style={{ fontWeight: 800, color: '#0369a1' }}>{pct(sm.ALL?.raw_signal_accuracy)}</span>
            <span style={{ color: '#64748b' }}> · traded-direction accuracy: </span>
            <span data-testid="ei-traded-accuracy-all" style={{ fontWeight: 700, color: '#0f172a' }}>{pct(sm.ALL?.traded_direction_accuracy)}</span>
            <span style={{ color: '#64748b' }}> · {sm.ALL?.determined || 0} graded / {sm.ALL?.total || 0} total fires ({data.period_days}d)</span>
          </div>

          <TierBlock title="TIER 1" emoji="➡️" s={sm.TIER1} accent="#16a34a" />
          <TierBlock title="TIER 2" emoji="🏀" s={sm.TIER2} accent="#d97706" />
          {sm.LEGACY?.total > 0 && <TierBlock title="LEGACY" emoji="📦" s={sm.LEGACY} accent="#64748b" />}

          <h3 style={{ fontSize: '1.1rem', color: '#0f172a', margin: '1.5rem 0 0.75rem' }}>Recent fires</h3>
          <div style={{ overflowX: 'auto' }}>
            <table data-testid="ei-fires-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr style={{ background: '#f1f5f9', textAlign: 'left' }}>
                  {['Time (UTC)', 'Pair', 'Tier', 'Signal→Traded', 'Range%', 'Outcome', 'Fav/Adv (p)', 'Traded OK?', 'Raw OK?'].map((h) => (
                    <th key={h} style={{ padding: '0.5rem 0.6rem', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(data.fires || []).map((f, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '0.45rem 0.6rem', whiteSpace: 'nowrap', color: '#64748b' }}>{(f.timestamp || '').slice(5, 16).replace('T', ' ')}</td>
                    <td style={{ padding: '0.45rem 0.6rem', fontWeight: 700 }}>{f.pair}</td>
                    <td style={{ padding: '0.45rem 0.6rem' }}>{f.tier === 'TIER1' ? '➡️ T1' : f.tier === 'TIER2' ? '🏀 T2' : '📦'}</td>
                    <td style={{ padding: '0.45rem 0.6rem', whiteSpace: 'nowrap' }}>{f.signal_direction || '?'} → <b>{f.traded_direction}</b>{f.inverted ? ' 🔁' : ''}</td>
                    <td style={{ padding: '0.45rem 0.6rem' }}>{f.range_position != null ? `${Math.round(f.range_position * 100)}%` : '—'}</td>
                    <td style={{ padding: '0.45rem 0.6rem', fontWeight: 700,
                      color: ['AGGRESSIVE_WIN', 'WIN'].includes(f.outcome_tag) ? '#16a34a' : f.outcome_tag === 'LOSS' ? '#dc2626' : '#b45309' }}>
                      {f.outcome_tag || 'PENDING'}
                    </td>
                    <td style={{ padding: '0.45rem 0.6rem', whiteSpace: 'nowrap' }}>{num(f.max_favorable_pips)} / {num(f.max_adverse_pips)}</td>
                    <td style={{ padding: '0.45rem 0.6rem', textAlign: 'center' }}>{dirMark(f.traded_correct)}</td>
                    <td style={{ padding: '0.45rem 0.6rem', textAlign: 'center' }}>{dirMark(f.raw_signal_correct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(!data.fires || data.fires.length === 0) && <div style={{ color: '#94a3b8', padding: '1rem 0' }}>No fires in this window.</div>}
          </div>
        </>
      )}
    </div>
  );
};

export default EarlyIgnitionTierStats;
